import { useState } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import { PrizeProducts, Variant } from '../interface/interface';

// Product interfaces
interface CreateProductParams {
  productName: string;
  productDescription: string;
  brandId: string;
  published: boolean;
  categories?: any[];
  images?: Array<{
    image: string;
    placeholder: string;
    isThumbnail: boolean;
  }>;
  tags?: string[];
  attributes?: Array<{
    attributeId: string;
    values: any;
  }>;
}

interface UpdateProductParams extends CreateProductParams {
  slug?: string;
}

interface ProductSearchParams {
  query?: string;
  filter?: any;
  sortBy?: string;
  sortAsc?: 'asc' | 'desc';
  page?: number;
  size?: number;
}

export interface PaginatedProducts {
  content: PrizeProducts[];
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
  size: number;
  number: number;
}

interface Attribute {
  value: string;
  label: string;
  attributeValues: any[];
}

interface Tag {
  id: string;
  tagName: string;
}

interface Brand {
  value: string;
  label: string;
}

interface VariantSearchParams {
  query?: string;
  filter?: any;
  page?: number;
  size?: number;
}

interface PaginatedVariants {
  content: Variant[];
  totalElements: number;
  totalPages: number;
  last: boolean;
  first: boolean;
  size: number;
  number: number;
  sort: any;
  numberOfElements: number;
  pageable: {
    pageNumber: number;
    pageSize: number;
    sort: any;
    offset: number;
    paged: boolean;
    unpaged: boolean;
  };
}

export const useProductAPI = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Create product (POST)
  const createProduct = async (
    params: CreateProductParams
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enhancedFetch(`${API.HOST}/api/v1/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          categories: params.categories || [],
          images:
            params.images?.map((image) => ({
              image: image.image,
              placeholder: image.placeholder,
              isThumbnail: image.isThumbnail
            })) || [],
          tags: params.tags || [],
          attributes:
            params.attributes?.map((attribute) => ({
              attributeId: attribute.attributeId,
              values: attribute.values
            })) || []
        })
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create product';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update product (PUT)
  const updateProduct = async (
    endpoint: string,
    params: UpdateProductParams
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enhancedFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          categories: params.categories || [],
          images:
            params.images?.map((image) => ({
              image: image.image,
              placeholder: image.placeholder,
              isThumbnail: image.isThumbnail
            })) || [],
          tags: params.tags || [],
          attributes:
            params.attributes?.map((attribute) => ({
              attributeId: attribute.attributeId,
              values: attribute.values
            })) || []
        })
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update product';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Search products with pagination (POST)
  const searchProducts = async (
    params: ProductSearchParams = {}
  ): Promise<PaginatedProducts | null> => {
    setSearchLoading(true);
    setError(null);

    try {
      const {
        page = 0,
        size = 30,
        query = '',
        filter = null,
        sortBy = 'id',
        sortAsc = 'asc'
      } = params;

      const data = await enhancedFetch(
        `${API.HOST}/api/v1/products/search?page=${page}&size=${size}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query,
            filter,
            sortBy,
            sortAsc
          })
        }
      );

      return {
        content:
          data?.content?.map((product: PrizeProducts) => ({
            id: product.id,
            slug: product.slug,
            productName: product.productName,
            productDescription: product.productDescription,
            brand: product.brand,
            updatedAt: product.updatedAt,
            galleries: product.galleries,
            productAttributes: product.productAttributes,
            published: product.published,
            tags: product.tags,
            status: product.status
          })) || [],
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        last: data.last,
        first: data.first,
        size: data.size,
        number: data.number
      };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to search products';
      setError(errorMessage);
      return null;
    } finally {
      setSearchLoading(false);
    }
  };

  // Get product by ID (GET)
  const getProductById = async (id: string): Promise<PrizeProducts | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await enhancedFetch(`${API.HOST}/api/v1/products/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return data as PrizeProducts;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch product';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get all products (GET)
  const getProducts = async (
    page: number = 0,
    size: number = 30
  ): Promise<PaginatedProducts | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await enhancedFetch(
        `${API.HOST}/api/v1/products?page=${page}&size=${size}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return data as PaginatedProducts;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch products';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Delete product (DELETE)
  const deleteProduct = async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enhancedFetch(`${API.HOST}/api/v1/products/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return true;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete product';
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get attributes (GET)
  const getAttributes = async (): Promise<Attribute[] | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await enhancedFetch(`${API.HOST}/api/v1/attributes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (Array.isArray(data)) {
        return data.map((attribute: any) => ({
          value: attribute.id,
          label: attribute.name,
          attributeValues: attribute.attributeValues
        }));
      }
      return [];
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch attributes';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get tags (GET)
  const getTags = async (): Promise<Tag[] | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await enhancedFetch(`${API.HOST}/api/v1/tags`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (Array.isArray(data)) {
        return data.map((tag: any) => ({
          id: tag.id,
          tagName: tag.tagName
        }));
      }
      return [];
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch tags';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Get brands (GET)
  const getBrands = async (): Promise<Brand[] | null> => {
    setLoading(true);
    setError(null);

    try {
      const data = await enhancedFetch(`${API.HOST}/api/v1/brands`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (Array.isArray(data)) {
        return data.map((brand: any) => ({
          value: brand.id,
          label: brand.name
        }));
      }
      return [];
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch brands';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Search variants with pagination (POST)
  const searchVariants = async (
    params: VariantSearchParams = {}
  ): Promise<PaginatedVariants | null> => {
    setSearchLoading(true);
    setError(null);

    try {
      const { page = 0, size = 30, query = '', filter = null } = params;

      let requestBody = {};
      let endpoint = '';

      if (query) {
        endpoint = `${API.HOST}/api/v1/variants/adminsearch`;
        requestBody = {
          query,
          ...filter
        };
      } else {
        endpoint = `${API.HOST}/api/v1/variants/adminsearch?page=${page}&size=${size}`;
        if (filter) {
          requestBody = {
            ...filter,
            page,
            size,
            query
          };
        }
      }

      const data = await enhancedFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (
        data &&
        data.paginatedVariants &&
        Array.isArray(data.paginatedVariants.content)
      ) {
        return data.paginatedVariants;
      }

      return null;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to search variants';
      setError(errorMessage);
      return null;
    } finally {
      setSearchLoading(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return {
    // Functions
    createProduct,
    updateProduct,
    searchProducts,
    searchVariants,
    getProductById,
    getProducts,
    deleteProduct,
    getAttributes,
    getTags,
    getBrands,
    clearError,

    // State
    loading,
    searchLoading,
    error
  };
};
